import React, { useState, useEffect } from "react";
import classes from "./cryptoTracker.module.css";

const CryptoPumpTracker = () => {
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(24);
  const [pumpThreshold, setPumpThreshold] = useState(50);

  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        setLoading(true);

        // Determine which price change parameter to use based on timeRange
        let priceChangeParam = "24h";
        if (timeRange === 1) {
          priceChangeParam = "1h";
        } else if (timeRange === 7) {
          priceChangeParam = "7d";
        } else if (timeRange === 14) {
          priceChangeParam = "14d";
        } else if (timeRange === 30) {
          priceChangeParam = "30d";
        }

        // Using CoinGecko's API to fetch market data with dynamic time parameter
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=250&page=1&sparkline=false&price_change_percentage=${priceChangeParam}`
        );

        if (!response.ok) {
          throw new Error("Too Many Requests, Please try after some time");
        }

        const data = await response.json();

        // Get the correct property name based on the time range
        const percentageField = `price_change_percentage_${priceChangeParam}`;

        // Filter for cryptocurrencies with percentage change greater than threshold
        const pumpedCryptos = data.filter(
          (crypto) => crypto[percentageField] > pumpThreshold
        );

        setCryptoData(pumpedCryptos);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchCryptoData();

    // Set up polling every 5 minutes
    const intervalId = setInterval(fetchCryptoData, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [timeRange, pumpThreshold]);

  if (loading) {
    return <div className={classes.loading}>Loading crypto data...</div>;
  }

  if (error) {
    return <div className={classes.error}>Error: {error}</div>;
  }

  const handleTimeRangeChange = (event) => {
    setTimeRange(Number(event.target.value));
  };

  const handleThresholdChange = (event) => {
    setPumpThreshold(Number(event.target.value));
  };

  // Get the appropriate time description
  const getTimeDescription = () => {
    if (timeRange === 1) return "hour";
    if (timeRange === 24) return "24 hours";
    if (timeRange === 7) return "7 days";
    if (timeRange === 14) return "14 days";
    if (timeRange === 30) return "30 days";
    return `${timeRange} hours`;
  };

  return (
    <div className={classes.container}>
      <h1 className={classes.title}>🚀 Crypto Pumps</h1>

      <div className={classes.controls}>
        <div className={classes.controlGroup}>
          <label htmlFor="timeRange" className={classes.label}>
            Time Range:
          </label>
          <select
            id="timeRange"
            className={classes.select}
            value={timeRange}
            onChange={handleTimeRangeChange}
          >
            <option value="1">Last 1 hour</option>
            <option value="24">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
          </select>
        </div>

        <div className={classes.controlGroup}>
          <label htmlFor="pumpThreshold" className={classes.label}>
            Pump Threshold:
          </label>
          <div className={classes.thresholdControl}>
            <input
              id="pumpThreshold"
              type="range"
              min="10"
              max="100"
              step="5"
              value={pumpThreshold}
              onChange={handleThresholdChange}
              className={classes.slider}
            />
            <span className={classes.thresholdValue}>{pumpThreshold}%</span>
          </div>
        </div>
      </div>

      <h2 className={classes.subtitle}>
        Showing coins with &gt;{pumpThreshold}% gains in last{" "}
        {getTimeDescription()}
      </h2>

      {cryptoData.length === 0 ? (
        <p className={classes.noPumps}>
          No cryptocurrencies have pumped more than {pumpThreshold}% in the last{" "}
          {getTimeDescription()}.
        </p>
      ) : (
        <div className={classes.cryptoGrid}>
          {cryptoData.map((crypto) => (
            <div key={crypto?.id} className={classes.cryptoCard}>
              <div className={classes.cryptoHeader}>
                <img
                  src={crypto.image}
                  alt={crypto.name}
                  className={classes.cryptoImage}
                />
                <div className={classes.cryptoInfo}>
                  <h2 className={classes.cryptoName}>{crypto.name}</h2>
                  <p className={classes.cryptoSymbol}>
                    {crypto?.symbol?.toUpperCase()}
                  </p>
                </div>
              </div>
              <div className={classes.cryptoStats}>
                <div className={classes.statItem}>
                  <p className={classes.statLabel}>Price</p>
                  <p className={classes.statValue}>
                    ${crypto.current_price.toLocaleString()}
                  </p>
                </div>
                <div className={classes.statItem}>
                  <p className={classes.statLabel}>
                    {timeRange === 24
                      ? "24h"
                      : timeRange === 1
                      ? "1h"
                      : timeRange === 7
                      ? "7d"
                      : timeRange === 14
                      ? "14d"
                      : "30d"}{" "}
                    Change
                  </p>
                  <p className={`${classes.statValue} ${classes.pumpValue}`}>
                    +
                    {crypto[
                      `price_change_percentage_${
                        timeRange === 24
                          ? "24h"
                          : timeRange === 1
                          ? "1h"
                          : timeRange === 7
                          ? "7d"
                          : timeRange === 14
                          ? "14d"
                          : "30d"
                      }`
                    ]?.toFixed(2)}
                    %
                  </p>
                </div>
                <div className={classes.statItem}>
                  <p className={classes.statLabel}>Market Cap</p>
                  <p className={classes.statValue}>
                    ${crypto?.market_cap?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className={classes.lastUpdated}>
        Last updated: {new Date().toLocaleString()}
      </p>
    </div>
  );
};

export default CryptoPumpTracker;
